import type { RichContent, RicosTheme } from '@wix/ricos';
import {
  pluginAnimationViewer,
  pluginCodeBlockViewer,
  pluginFontFamilyViewer,
  pluginIndentViewer,
  pluginLineSpacingViewer,
  pluginLinkViewer,
  pluginMentionsViewer,
  pluginSpoilerViewer,
  pluginTextColorViewer,
  pluginTextHighlightViewer,
  RicosViewer,
} from '@wix/ricos';
import '@wix/ricos/css/all-plugins-viewer.css';
import type { Theme } from '@wix/ricos/internal';
import {
  calculateClamp,
  ensureCardsUnwrapped,
  MIN_FONT_SIZE_RATIO,
  pluginCardViewer,
} from '@wix/ricos/internal';
import {
  pluginActionButtonLazy,
  pluginAudioLazy,
  pluginCollapsibleListLazy,
  pluginDividerLazy,
  pluginFileUploadLazy,
  pluginGalleryLazy,
  pluginGiphyLazy,
  pluginHtmlLazy,
  pluginImageLazy,
  pluginLinkButtonLazy,
  pluginLinkPreviewLazy,
  pluginPollLazy,
  pluginShapeLazy,
  pluginSmartBlockLazy,
  pluginTableLazy,
  pluginVerticalEmbedLazy,
  pluginVideoLazy,
} from '@wix/ricos/lazy';
import {
  designToPageCssProperties,
  designToRicosTheme,
  withWixelThemeFontFallback,
} from '@wix/www-ricos/design';
import React from 'react';

import './App.css';
import { WIXEL_FONT_FALLBACK } from './font-family';
import { FontsCssLoader } from './fonts-css-loader';

const getColorPalette = (design: Theme): string[] => {
  const bg = design?.background?.colorOptions?.color?.colorOptions as string | undefined;
  const palette = (design?.colorPalette ?? []) as string[];
  return [bg, ...palette.slice(0, 6)].filter((c): c is string => !!c);
};

const getPlugins = ({
  pageFormat,
  isPagesEnabled,
  design,
  isPrint,
}: {
  pageFormat?: string;
  isPagesEnabled: boolean;
  design: Theme;
  isPrint?: boolean;
}) => [
  {
    type: 'fontSize' as const,
    overrideInlineFontSize: (value: string) => {
      return calculateClamp({
        maxSize: parseInt(value),
        minSize: parseInt(value) * MIN_FONT_SIZE_RATIO,
        // In print, size fonts relative to the fixed 900px content container (cqw) rather than
        // the viewport (vw). The external PDF capturer measures content height at a viewport
        // width that can differ from the 900px print box; vw-based fonts then reflow taller at
        // capture time, pushing trailing content (e.g. the card footer) past the measured page
        // height and clipping it. Container-relative sizing keeps the measured and rasterized
        // heights identical. See WIXELDOCS-178.
        relativeTo: isPrint ? 'container' : 'viewport',
      });
    },
  },
  ...(isPagesEnabled ? [pluginCardViewer({ pageFormat })] : []),
  pluginActionButtonLazy(),
  // Skipped in print: the plugin's reveal contract SSR-hides entrance-animated nodes
  // (visibility:hidden) until an IntersectionObserver plays them, but the PDF capturer
  // never scrolls and printReady() doesn't wait for reveals — below-viewport nodes would
  // print as blank gaps. Absent the plugin, animated nodes render statically visible.
  ...(isPrint ? [] : [pluginAnimationViewer()]),
  pluginAudioLazy(),
  pluginCodeBlockViewer(),
  pluginCollapsibleListLazy(),
  pluginFontFamilyViewer({ fallbackFontFamily: WIXEL_FONT_FALLBACK }),
  pluginDividerLazy(),
  pluginGalleryLazy(),
  pluginGiphyLazy(),
  pluginHtmlLazy({
    getColorPalette: () => getColorPalette(design),
  }),
  pluginImageLazy(),
  pluginIndentViewer(),
  pluginLineSpacingViewer(),
  pluginLinkViewer(),
  pluginLinkButtonLazy(),
  pluginActionButtonLazy(),
  pluginLinkPreviewLazy(),
  pluginMentionsViewer(),
  pluginSmartBlockLazy(),
  pluginSpoilerViewer(),
  pluginTableLazy(),
  pluginTextColorViewer(),
  pluginTextHighlightViewer(),
  pluginVerticalEmbedLazy(),
  pluginVideoLazy(),
  pluginFileUploadLazy(),
  pluginPollLazy(),
  pluginShapeLazy(),
];

type AppProps = {
  content: RichContent;
  design: Theme;
  isPrint?: boolean;
  instance?: string;
  pageFormat?: string;
  isPagesEnabled: boolean;
};

export const App: React.FC<AppProps> = ({
  content,
  design,
  isPrint,
  instance,
  pageFormat,
  isPagesEnabled,
}) => {
  const viewerContent = React.useMemo(
    () => (isPagesEnabled ? content : ensureCardsUnwrapped(content)),
    [content, isPagesEnabled]
  );
  const plugins = React.useMemo(
    () => getPlugins({ pageFormat, isPagesEnabled, design, isPrint }),
    [pageFormat, isPagesEnabled, design, isPrint]
  );
  // Print uses container-relative ('cqw') font sizing so the rendered height does not depend on
  // the PDF capturer's viewport width (which otherwise clips trailing content — see WIXELDOCS-178
  // and getPlugins above). This also aligns print with the editor and doc-export-preview, which
  // already render with 'cqw'. The on-screen published viewer keeps viewport-relative ('vw').
  const initialTheme = React.useMemo(
    () =>
      designToRicosTheme(
        withWixelThemeFontFallback(design),
        undefined,
        isPrint ? 'cqw' : 'vw',
        isPagesEnabled
      ),
    [design, isPagesEnabled, isPrint]
  );
  const theme = React.useMemo((): RicosTheme => {
    const contentWidth = '900px';

    return {
      ...initialTheme,
      breakout: initialTheme.breakout
        ? {
            ...initialTheme.breakout,
            normalPadding: {
              start: `max(calc(((100% - ${contentWidth}) / 2) + var(--rt-design-horizontal-padding-start)), var(--rt-design-horizontal-padding-start))`,
              end: `max(calc(((100% - ${contentWidth}) / 2) + var(--rt-design-horizontal-padding-end)), var(--rt-design-horizontal-padding-end))`,
            },
          }
        : undefined,
    };
  }, [initialTheme]);
  const cssProperties = React.useMemo(() => {
    const paddingStart = initialTheme.breakout?.normalPadding.start;
    const paddingEnd = initialTheme.breakout?.normalPadding.end;

    const backgroundStyles = isPagesEnabled
      ? {}
      : {
          backgroundColor: 'var(--rt-design-background-color, #FFFFFF)',
          backgroundImage:
            'var(--rt-design-background-gradient, var(--rt-design-background-image, none))',
          backgroundSize: 'var(--rt-design-background-image-scaling, cover)',
          backgroundPosition: 'var(--rt-design-background-image-position, center)',
          backgroundRepeat: 'no-repeat',
        };

    return {
      ...designToPageCssProperties(design, 'desktop'),
      '--rt-design-internal-horizontal-padding-start': paddingStart,
      '--rt-design-internal-horizontal-padding-end': paddingEnd,
      '--ricos-font-family': WIXEL_FONT_FALLBACK,
      ...backgroundStyles,
    };
  }, [initialTheme, design, isPagesEnabled]);

  return (
    <div className="editorContainer" style={cssProperties}>
      <FontsCssLoader />
      <RicosViewer
        plugins={plugins}
        content={viewerContent}
        theme={theme}
        isPrint={isPrint}
        instance={instance}
      />
    </div>
  );
};
